import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import { IconMagnifierOutline18 as SearchIcon } from "nucleo-ui-outline-18"

import type { AiTokenFeature } from "@productlane/lib"
import {
	AI_COST_PER_RESOLUTION_CENTS,
	AI_TOKEN_FEATURE_LABELS,
	TOKEN_MARKUP,
} from "@productlane/lib"
import {
	Avatar,
	Badge,
	Input,
	SortableTableHeader,
	Tabs,
	TabsList,
	TabsTrigger,
	Tooltip,
	Virtualizer,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { ReportingChartFrame } from "@/components/reporting/charts"
import { api } from "@/lib/api"

type Period = "month" | "lastMonth" | "all"

type Row = RouterOutputs["analytics"]["aiSpendByWorkspace"]["rows"][number]

type NumberColumnKey = "limit" | "cost" | "charge" | "invoice" | "net"

type SortType =
	| `${NumberColumnKey}-asc`
	| `${NumberColumnKey}-desc`
	| "tokens-asc"
	| "tokens-desc"
	| "resolutions-asc"
	| "resolutions-desc"
	| "name-asc"
	| "name-desc"

const NUMBER_COLUMNS = [
	{
		key: "cost",
		label: "At cost",
		width: "w-20",
		hint: "Provider price of these tokens. This is what we pay Anthropic and OpenAI, and what the same usage would cost them with no fee from us. Support agent tokens sit outside it because they bill per resolution. Thread titles are free on every plan, so they never reach a charge.",
	},
	{
		key: "charge",
		label: "We charge",
		width: "w-20",
		hint: `The same tokens at our rate: provider cost x${TOKEN_MARKUP}. This is the number that eats their credits and their cap.`,
	},
	{
		key: "limit",
		label: "Used of limit",
		width: "w-28",
		hint: "How much of their monthly AI allowance the charged spend used up. The allowance is the credits included in their plan plus the overage cap they set, and AI pauses once they reach it.",
	},
	{
		key: "invoice",
		label: "We invoice",
		width: "w-20",
		hint: "What the monthly AI invoice job will put on their Stripe invoice: token spend above their credits, clamped to their overage cap, plus resolutions beyond the free tier. Trials and the free plan are invoiced nothing.",
	},
	{
		key: "net",
		label: "Net",
		width: "w-20",
		hint: "We invoice minus everything we pay the providers, support agent and free features included. Negative means credits and free tiers absorbed the usage, so we paid for it.",
	},
] as const satisfies ReadonlyArray<{
	key: NumberColumnKey
	label: string
	width: string
	hint: string
}>

const PERIODS = [
	{ value: "month", label: "This month" },
	{ value: "lastMonth", label: "Last month" },
	{ value: "all", label: "All time" },
] as const satisfies ReadonlyArray<{ value: Period; label: string }>

const usd = (value: number) =>
	`$${value.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`

const compactTokens = (value: number) =>
	value.toLocaleString("en-US", {
		notation: "compact",
		maximumFractionDigits: 1,
	})

const featureLabel = (feature: string) =>
	AI_TOKEN_FEATURE_LABELS[feature as AiTokenFeature] ?? feature

const planLabel = (row: Row) => (row.trialing ? "trial" : row.plan)

// Share of the spend limit used. null limit is uncapped, a 0 limit means
// billable AI is off, so any spend there is already past the line.
function limitShare(row: Row): number | null {
	if (row.limitUsd === null) return null
	if (row.limitUsd === 0) return row.meteredUsd > 0 ? 1 : 0
	return row.meteredUsd / row.limitUsd
}

function limitCellLabel(row: Row): string {
	if (row.limitUsd === null) return "no cap"
	if (row.limitUsd === 0) return "AI off"
	const share = limitShare(row) ?? 0
	return `${Math.round(share * 100)}% of ${usd(row.limitUsd)}`
}

function limitToneClass(row: Row): string {
	const share = limitShare(row)
	if (share === null) return "text-secondary"
	if (share >= 1) return "text-red-500"
	if (share >= 0.8) return "text-amber-500"
	return "text-secondary"
}

function DetailTooltip({
	rows,
	note,
}: {
	rows: [string, string][]
	note?: string
}) {
	return (
		<div className="flex w-56 flex-col gap-1.5 py-0.5">
			<div className="flex flex-col gap-0.5">
				{rows.map(([label, value]) => (
					<div
						key={label}
						className="flex items-baseline justify-between gap-3"
					>
						<span className="text-tertiary">{label}</span>
						<span className="tabular-nums">{value}</span>
					</div>
				))}
			</div>
			{note ? <p className="text-tertiary">{note}</p> : null}
		</div>
	)
}

function limitTooltip(row: Row): ReactNode {
	const share = limitShare(row)
	const rows: [string, string][] = [["We charge them", usd(row.meteredUsd)]]

	if (row.trialing) {
		rows.push(["Trial credit", usd(row.creditsUsd)])
		return (
			<DetailTooltip
				rows={rows}
				note="A trial gets this credit and no overage, so AI pauses once it runs out."
			/>
		)
	}

	if (row.limitUsd === 0) {
		return (
			<DetailTooltip
				rows={rows}
				note="Billable AI is off on the free plan, so this spend is not growing."
			/>
		)
	}

	rows.push([`Credits on ${row.plan}`, usd(row.creditsUsd)])
	if (row.limitUsd === null) {
		return (
			<DetailTooltip
				rows={rows}
				note="They set no overage cap, so spend above the credits runs uncapped."
			/>
		)
	}

	rows.push(["Overage cap they set", usd(row.overageCapUsd ?? 0)])
	rows.push(["AI pauses at", usd(row.limitUsd)])
	return (
		<DetailTooltip
			rows={rows}
			note={
				share === null
					? undefined
					: `${Math.round(share * 100)}% of the limit used.`
			}
		/>
	)
}

function resolutionsTooltip(row: Row): ReactNode {
	return (
		<DetailTooltip
			rows={[
				["Resolutions", row.resolutions.toLocaleString("en-US")],
				[
					"Billable beyond the plan",
					row.billableResolutions.toLocaleString("en-US"),
				],
				[
					`At $${(AI_COST_PER_RESOLUTION_CENTS / 100).toFixed(2)} each`,
					usd(row.invoicedResolutionsUsd),
				],
			]}
			note="Support agent resolutions in this period. The plan's free tier is on us; the rest bills per resolution when pay per use is on."
		/>
	)
}

function costTooltip(row: Row): ReactNode {
	return (
		<DetailTooltip
			rows={[
				["Token-billed features", usd(row.atCostUsd)],
				["Free features", usd(row.freeCostUsd)],
				["Support agent", usd(row.agentCostUsd)],
				["Total we pay", usd(row.providerCostUsd)],
			]}
			note="Support agent tokens earn per billable resolution instead of the token meter. Free features like thread titles are ours to pay for."
		/>
	)
}

function chargeTooltip(row: Row): ReactNode {
	return (
		<DetailTooltip
			rows={[
				["At cost", usd(row.atCostUsd)],
				[`Our fee (x${TOKEN_MARKUP})`, usd(row.meteredUsd - row.atCostUsd)],
				["We charge them", usd(row.meteredUsd)],
			]}
			note="Without our fee the same usage would cost them the at-cost figure."
		/>
	)
}

function invoiceTooltip(row: Row): ReactNode {
	if (row.trialing || row.plan === "free") {
		return (
			<DetailTooltip
				rows={[
					["We charge them", usd(row.meteredUsd)],
					["On their invoice", usd(0)],
				]}
				note={
					row.trialing
						? "Trials are invoiced nothing; the trial credit is the hard stop."
						: "The free plan is invoiced nothing."
				}
			/>
		)
	}

	const rows: [string, string][] = [
		["We charge them", usd(row.meteredUsd)],
		["Included credits", `-${usd(row.creditsUsd)}`],
	]
	if (row.cappedUsd > 0) {
		rows.push(["Cut by their cap", `-${usd(row.cappedUsd)}`])
	}
	rows.push(["Token overage", usd(row.invoicedTokensUsd)])
	if (row.invoicedResolutionsUsd > 0) {
		rows.push([
			`${row.billableResolutions.toLocaleString("en-US")} paid resolutions`,
			usd(row.invoicedResolutionsUsd),
		])
	}
	rows.push(["On their invoice", usd(row.invoicedUsd)])

	return (
		<DetailTooltip
			rows={rows}
			note={
				row.invoicedUsd === 0
					? "Their usage fits inside the credits, so they are billed nothing for AI."
					: undefined
			}
		/>
	)
}

function netTooltip(row: Row): ReactNode {
	return (
		<DetailTooltip
			rows={[
				["We invoice", usd(row.invoicedUsd)],
				["Total we pay", `-${usd(row.providerCostUsd)}`],
				["Net", usd(row.netUsd)],
			]}
			note={
				row.netUsd < 0
					? "We are covering this usage out of their credits and free tiers."
					: undefined
			}
		/>
	)
}

export function AISpendByWorkspaceTable() {
	const [period, setPeriod] = useState<Period>("month")
	const [sortType, setSortType] = useState<SortType>("cost-desc")
	const [search, setSearch] = useState("")

	const query = api.analytics.aiSpendByWorkspace.useQuery(
		{ period },
		{ staleTime: 30000 },
	)

	// Credits and caps reset monthly, so everything measured against them is
	// undefined once the range spans several months.
	const isMonthly = period !== "all"

	const rows = useMemo(() => {
		const term = search.trim().toLowerCase()
		const filtered = term
			? (query.data?.rows ?? []).filter(
					(row) =>
						row.name.toLowerCase().includes(term) ||
						(row.domain?.toLowerCase().includes(term) ?? false),
				)
			: (query.data?.rows ?? [])

		const direction = sortType.endsWith("-asc") ? 1 : -1
		const key = sortType.slice(0, sortType.lastIndexOf("-"))

		return [...filtered].sort((a, b) => {
			if (key === "name") return direction * a.name.localeCompare(b.name)
			const value = (row: (typeof filtered)[number]) => {
				if (key === "limit") return limitShare(row) ?? -1
				if (key === "charge") return row.meteredUsd
				if (key === "invoice") return row.invoicedUsd
				if (key === "net") return row.netUsd
				if (key === "tokens") return row.inputTokens + row.outputTokens
				if (key === "resolutions") return row.resolutions
				return row.atCostUsd
			}
			return direction * (value(a) - value(b))
		})
	}, [query.data?.rows, search, sortType])

	const totals = useMemo(() => {
		if (!query.data) return null
		if (!search.trim()) return query.data.totals
		return rows.reduce(
			(acc, row) => ({
				workspaces: acc.workspaces + 1,
				inputTokens: acc.inputTokens + row.inputTokens,
				outputTokens: acc.outputTokens + row.outputTokens,
				resolutions: acc.resolutions + row.resolutions,
				providerCostUsd: acc.providerCostUsd + row.providerCostUsd,
				agentCostUsd: acc.agentCostUsd + row.agentCostUsd,
				freeCostUsd: acc.freeCostUsd + row.freeCostUsd,
				atCostUsd: acc.atCostUsd + row.atCostUsd,
				meteredUsd: acc.meteredUsd + row.meteredUsd,
				invoicedTokensUsd: acc.invoicedTokensUsd + row.invoicedTokensUsd,
				invoicedResolutionsUsd:
					acc.invoicedResolutionsUsd + row.invoicedResolutionsUsd,
				cappedUsd: acc.cappedUsd + row.cappedUsd,
				invoicedUsd: acc.invoicedUsd + row.invoicedUsd,
				netUsd: acc.netUsd + row.netUsd,
			}),
			{
				workspaces: 0,
				inputTokens: 0,
				outputTokens: 0,
				resolutions: 0,
				providerCostUsd: 0,
				agentCostUsd: 0,
				freeCostUsd: 0,
				atCostUsd: 0,
				meteredUsd: 0,
				invoicedTokensUsd: 0,
				invoicedResolutionsUsd: 0,
				cappedUsd: 0,
				invoicedUsd: 0,
				netUsd: 0,
			},
		)
	}, [query.data, rows, search])

	return (
		<ReportingChartFrame
			title="AI spend by workspace"
			description="Real metered token usage per workspace. Resolutions counts support agent resolutions in the period; those beyond the plan's free tier bill per resolution when pay per use is on. Used of limit is how much of their monthly AI allowance they burned: plan credits plus the overage cap they set. At cost is what the tokens cost us, which is also what they would pay with no fee from us. We charge is the same tokens at our rate. We invoice runs the same math as the monthly invoice job, so it matches what lands on their Stripe invoice: token overage clamped to their cap plus paid resolutions, with trials and the free plan at zero. Net is we invoice minus everything we pay the providers. Credits reset monthly, so the limit, invoiced and net columns are blank on all time."
			isLoading={query.isLoading}
			isEmpty={!query.isLoading && rows.length === 0}
			emptyMessage={
				search ? "No workspace matches this search" : "No token usage recorded"
			}
			legend={
				<div className="flex items-center gap-2">
					<Input
						name="ai-spend-workspace-search"
						variant="borderless"
						textSize="sm"
						icon={SearchIcon}
						placeholder="Find workspace..."
						value={search}
						className="w-44 px-0 placeholder:text-tertiary"
						inputClassName="h-7"
						onChange={(event) => setSearch(event.target.value)}
					/>
					<Tabs
						value={period}
						onValueChange={(value) => setPeriod(value as Period)}
					>
						<TabsList variant="transparent">
							{PERIODS.map((option) => (
								<TabsTrigger
									key={option.value}
									variant="transparent"
									value={option.value}
								>
									{option.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>
			}
		>
			<div className="text-sm">
				<div className="flex items-center gap-3 border-b border-gray-200 pb-2 text-xs text-tertiary dark:border-gray-700/80">
					<div className="min-w-0 flex-1">
						<SortableTableHeader
							label="Workspace"
							currentSortType={sortType}
							sortTypeAsc="name-asc"
							sortTypeDesc="name-desc"
							onClick={setSortType}
						/>
					</div>
					<div className="w-16 flex-none">Plan</div>
					<div className="w-20 flex-none">
						<SortableTableHeader
							label="Tokens"
							currentSortType={sortType}
							sortTypeAsc="tokens-asc"
							sortTypeDesc="tokens-desc"
							onClick={setSortType}
							arrowPosition="left"
							className="ml-auto"
						/>
					</div>
					<div className="w-20 flex-none">
						<Tooltip
							label={`Support agent resolutions in the period. Beyond the plan's free tier each bills $${(AI_COST_PER_RESOLUTION_CENTS / 100).toFixed(2)} when pay per use is on, and that lands in the We invoice column.`}
						>
							<SortableTableHeader
								label="Resolutions"
								currentSortType={sortType}
								sortTypeAsc="resolutions-asc"
								sortTypeDesc="resolutions-desc"
								onClick={setSortType}
								arrowPosition="left"
								className="ml-auto"
							/>
						</Tooltip>
					</div>
					{NUMBER_COLUMNS.map((column) => (
						<div key={column.key} className={`${column.width} flex-none`}>
							<Tooltip label={column.hint}>
								<SortableTableHeader
									label={column.label}
									currentSortType={sortType}
									sortTypeAsc={`${column.key}-asc`}
									sortTypeDesc={`${column.key}-desc`}
									onClick={setSortType}
									arrowPosition="left"
									className="ml-auto"
								/>
							</Tooltip>
						</div>
					))}
					<div className="w-32 flex-none">Top feature</div>
				</div>

				<Virtualizer
					className="max-h-[420px] pb-2"
					estimateSize={36}
					itemsData={rows}
					itemContent={(row) => (
						<div className="flex items-center gap-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-850/50">
							<div className="flex min-w-0 flex-1 items-center gap-2">
								<Avatar
									size="s"
									name={row.name || row.domain || ""}
									image={row.logoUrl}
								/>
								<span className="truncate">{row.name || row.domain}</span>
							</div>
							<div className="w-16 flex-none">
								<Tooltip
									enabled={row.trialing}
									label="Stripe reports this subscription as trialing, so it runs on the trial credit with no overage."
								>
									<Badge variant={row.trialing ? "green" : "default"} size="sm">
										{planLabel(row)}
									</Badge>
								</Tooltip>
							</div>
							<Tooltip
								label={`${row.inputTokens.toLocaleString("en-US")} in / ${row.outputTokens.toLocaleString("en-US")} out`}
							>
								<div className="w-20 flex-none text-right tabular-nums text-secondary">
									{compactTokens(row.inputTokens + row.outputTokens)}
								</div>
							</Tooltip>
							{row.resolutions > 0 ? (
								<Tooltip label={resolutionsTooltip(row)}>
									<div className="w-20 flex-none text-right tabular-nums">
										{row.resolutions.toLocaleString("en-US")}
									</div>
								</Tooltip>
							) : (
								<div className="w-20 flex-none text-right tabular-nums text-tertiary">
									—
								</div>
							)}
							<Tooltip label={costTooltip(row)}>
								<div className="w-20 flex-none text-right tabular-nums">
									{usd(row.atCostUsd)}
								</div>
							</Tooltip>
							<Tooltip label={chargeTooltip(row)}>
								<div className="w-20 flex-none text-right tabular-nums text-secondary">
									{usd(row.meteredUsd)}
								</div>
							</Tooltip>
							{isMonthly ? (
								<Tooltip label={limitTooltip(row)}>
									<div
										className={`w-28 flex-none text-right tabular-nums ${limitToneClass(row)}`}
									>
										{limitCellLabel(row)}
									</div>
								</Tooltip>
							) : (
								<div className="w-28 flex-none text-right tabular-nums text-tertiary">
									—
								</div>
							)}
							{isMonthly ? (
								<Tooltip label={invoiceTooltip(row)}>
									<div className="w-20 flex-none text-right tabular-nums text-secondary">
										{usd(row.invoicedUsd)}
									</div>
								</Tooltip>
							) : (
								<div className="w-20 flex-none text-right tabular-nums text-tertiary">
									—
								</div>
							)}
							{isMonthly ? (
								<Tooltip label={netTooltip(row)}>
									<div
										className={
											row.netUsd < 0
												? "w-20 flex-none text-right font-medium tabular-nums text-red-500"
												: "w-20 flex-none text-right font-medium tabular-nums"
										}
									>
										{usd(row.netUsd)}
									</div>
								</Tooltip>
							) : (
								<div className="w-20 flex-none text-right tabular-nums text-tertiary">
									—
								</div>
							)}
							<div className="w-32 flex-none truncate text-tertiary">
								{row.features[0] ? featureLabel(row.features[0].feature) : "—"}
							</div>
						</div>
					)}
				/>

				{totals ? (
					<div className="flex items-center gap-3 border-t border-gray-200 pt-2 text-xs dark:border-gray-700/80">
						<div className="min-w-0 flex-1 text-tertiary">
							{totals.workspaces.toLocaleString("en-US")} workspaces with usage
						</div>
						<div className="w-16 flex-none" />
						<div className="w-20 flex-none text-right tabular-nums text-tertiary">
							{compactTokens(totals.inputTokens + totals.outputTokens)}
						</div>
						<div className="w-20 flex-none text-right font-medium tabular-nums">
							{totals.resolutions.toLocaleString("en-US")}
						</div>
						<div className="w-20 flex-none text-right font-medium tabular-nums">
							{usd(totals.atCostUsd)}
						</div>
						<div className="w-20 flex-none text-right tabular-nums text-secondary">
							{usd(totals.meteredUsd)}
						</div>
						<div className="w-28 flex-none" />
						<div className="w-20 flex-none text-right tabular-nums text-secondary">
							{isMonthly ? usd(totals.invoicedUsd) : "—"}
						</div>
						<div
							className={
								isMonthly && totals.netUsd < 0
									? "w-20 flex-none text-right font-medium tabular-nums text-red-500"
									: "w-20 flex-none text-right font-medium tabular-nums"
							}
						>
							{isMonthly ? usd(totals.netUsd) : "—"}
						</div>
						<div className="w-32 flex-none" />
					</div>
				) : null}
			</div>
		</ReportingChartFrame>
	)
}
